import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import CollapsibleCard from './CollapsibleCard';
import { CheckIcon, DownloadIcon, ImageIcon, SpinnerIcon } from '../icons';
import type { ImageGenerationPart } from './types';

const ImageGenerationResult: React.FC<{
  imageGeneration: ImageGenerationPart;
}> = ({ imageGeneration }) => {
  const { colors } = useTheme();
  const { getAccessToken } = useAuth();
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [imageData, setImageData] = useState<{
    originalPrompt: string;
    revisedPrompt?: string;
    imageUrl: string;
    downloadUrl?: string;
    blobName?: string;
    isBase64: boolean;
  } | null>(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const fetchedRef = useRef(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (fetchedRef.current) 
      return;

    const loadImage = async () => {
      const originalPrompt = imageGeneration.original_prompt;
      const revisedPrompt = imageGeneration.revised_prompt || undefined;
      const firstImage = imageGeneration.images?.[0];

      if (!firstImage) 
        return;

      if (firstImage.downloadUrl && !firstImage.base64) {
        setIsLoadingImage(true);
        fetchedRef.current = true;
        try {
          const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
          const absoluteUrl = firstImage.downloadUrl.startsWith('http')
            ? firstImage.downloadUrl
            : `${API_BASE_URL}${firstImage.downloadUrl}`;

          const accessToken = await getAccessToken();
          const response = await fetch(absoluteUrl, {
            method: 'GET',
            credentials: 'include',
            headers: {
              ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
            }
          });

          if (response.ok) {
            const blob = await response.blob();
            const imageUrl = URL.createObjectURL(blob);
            objectUrlRef.current = imageUrl;
            setImageData({
              originalPrompt,
              revisedPrompt,
              imageUrl,
              downloadUrl: firstImage.downloadUrl,
              blobName: firstImage.blobName,
              isBase64: false
            });
          } else {
            console.error('Failed to fetch image from blob storage');
          }
        } catch (error) {
          console.error('Error fetching image:', error);
        } finally {
          setIsLoadingImage(false);
        }
      } else if (firstImage.base64) {
        fetchedRef.current = true;
        const imageUrl = `data:image/png;base64,${firstImage.base64}`;
        setImageData({ originalPrompt, revisedPrompt, imageUrl, isBase64: true });
      }
    };

    loadImage();

    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [imageGeneration]);

  const handleDownloadImage = async () => {
    if (!imageData) 
      return;

    try {
      if (imageData.isBase64) {
        const base64Data = imageData.imageUrl.split(',')[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/png' });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `generated-image-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (imageData.downloadUrl) {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const absoluteUrl = imageData.downloadUrl.startsWith('http')
          ? imageData.downloadUrl
          : `${API_BASE_URL}${imageData.downloadUrl}`;

        const accessToken = await getAccessToken();
        const response = await fetch(absoluteUrl, {
          method: 'GET',
          credentials: 'include',
          headers: {
            ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
          }
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          
          link.href = url;
          link.download = `generated-image-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else {
          console.error('Failed to download image from blob storage');
          return;
        }
      }

      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 2000);
    } catch (error) {
      console.error('Failed to download image:', error);
    }
  };

  if (!imageData && !isLoadingImage) {
    return null;
  }

  return (
    <CollapsibleCard
      icon={<ImageIcon size={16} />}
      title="Image generated"
      defaultExpanded
      actions={
        <button
          type="button"
          className={`cv-tool-btn${downloadSuccess ? ' is-done' : ''}`}
          onClick={handleDownloadImage}
          title="Download image"
        >
          {downloadSuccess ? <CheckIcon size={14} /> : <DownloadIcon size={14} />}
          {downloadSuccess ? 'Downloaded' : 'Download'}
        </button>
      }
    >
      <div style={{ padding: '16px' }}>
        {imageData && (
          <div style={{ fontSize: '13px', color: colors.textPrimary, marginBottom: '12px', lineHeight: '1.5' }}>
            <div style={{
              fontWeight: 600, marginBottom: '4px', color: colors.textMuted,
              fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px'
            }}>
              Prompt
            </div>
            <div style={{ marginBottom: '8px' }}>{imageData.originalPrompt}</div>

            {imageData.revisedPrompt && (
              <>
                <div style={{
                  fontWeight: 600, marginBottom: '4px', marginTop: '12px', color: colors.textMuted,
                  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px'
                }}>
                  Revised Prompt
                </div>
                <div style={{
                  padding: '8px 12px', backgroundColor: colors.surfaceElevated,
                  borderRadius: '6px', fontSize: '12px', fontStyle: 'italic', color: colors.textMuted
                }}>
                  {imageData.revisedPrompt}
                </div>
              </>
            )}
          </div>
        )}

        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          marginTop: '12px', minHeight: isLoadingImage ? '200px' : 'auto'
        }}>
          {isLoadingImage ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <span style={{ color: colors.primary, display: 'flex' }}><SpinnerIcon size={32} /></span>
              <span style={{ color: colors.textMuted, fontSize: '13px' }}>Loading image…</span>
            </div>
          ) : imageData ? (
            <img
              src={imageData.imageUrl}
              alt="Generated"
              style={{
                maxWidth: '100%', height: 'auto',
                borderRadius: '8px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}
            />
          ) : null}
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default ImageGenerationResult;